// src/apps/secret/components/receipt/country-codes.ts
//
// Static list of dialing codes used by the receipt's WhatsApp share panel.
// Kept intentionally offline (no network lookup): a curated set of the most
// common destinations for a Brazilian audience, with Brazil first and used as
// the default selection.
//
// `flag` is a Unicode regional-indicator emoji so no image assets are needed.
// `name` is in Portuguese to match the SeuID brand's primary locale.

export interface CountryCode {
  /** ISO 3166-1 alpha-2 code, used as a stable option key. */
  iso: string;
  /** Country name (pt_BR). */
  name: string;
  /** International dialing code, digits only, without the leading "+". */
  dial: string;
  /** Flag emoji (regional-indicator pair). */
  flag: string;
}

/**
 * Curated dialing-code list. Brazil is first so it can be the default
 * selection in the WhatsApp panel.
 */
export const COUNTRY_CODES: CountryCode[] = [
  { iso: 'BR', name: 'Brasil', dial: '55', flag: '🇧🇷' },
  { iso: 'PT', name: 'Portugal', dial: '351', flag: '🇵🇹' },
  { iso: 'US', name: 'Estados Unidos', dial: '1', flag: '🇺🇸' },
  { iso: 'AR', name: 'Argentina', dial: '54', flag: '🇦🇷' },
  { iso: 'AO', name: 'Angola', dial: '244', flag: '🇦🇴' },
  { iso: 'AU', name: 'Austrália', dial: '61', flag: '🇦🇺' },
  { iso: 'BE', name: 'Bélgica', dial: '32', flag: '🇧🇪' },
  { iso: 'BO', name: 'Bolívia', dial: '591', flag: '🇧🇴' },
  { iso: 'CA', name: 'Canadá', dial: '1', flag: '🇨🇦' },
  { iso: 'CL', name: 'Chile', dial: '56', flag: '🇨🇱' },
  { iso: 'CN', name: 'China', dial: '86', flag: '🇨🇳' },
  { iso: 'CO', name: 'Colômbia', dial: '57', flag: '🇨🇴' },
  { iso: 'CV', name: 'Cabo Verde', dial: '238', flag: '🇨🇻' },
  { iso: 'DE', name: 'Alemanha', dial: '49', flag: '🇩🇪' },
  { iso: 'ES', name: 'Espanha', dial: '34', flag: '🇪🇸' },
  { iso: 'FR', name: 'França', dial: '33', flag: '🇫🇷' },
  { iso: 'GB', name: 'Reino Unido', dial: '44', flag: '🇬🇧' },
  { iso: 'IE', name: 'Irlanda', dial: '353', flag: '🇮🇪' },
  { iso: 'IN', name: 'Índia', dial: '91', flag: '🇮🇳' },
  { iso: 'IT', name: 'Itália', dial: '39', flag: '🇮🇹' },
  { iso: 'JP', name: 'Japão', dial: '81', flag: '🇯🇵' },
  { iso: 'MX', name: 'México', dial: '52', flag: '🇲🇽' },
  { iso: 'MZ', name: 'Moçambique', dial: '258', flag: '🇲🇿' },
  { iso: 'NL', name: 'Países Baixos', dial: '31', flag: '🇳🇱' },
  { iso: 'PY', name: 'Paraguai', dial: '595', flag: '🇵🇾' },
  { iso: 'PE', name: 'Peru', dial: '51', flag: '🇵🇪' },
  { iso: 'UY', name: 'Uruguai', dial: '598', flag: '🇺🇾' },
  { iso: 'VE', name: 'Venezuela', dial: '58', flag: '🇻🇪' },
  { iso: 'ZA', name: 'África do Sul', dial: '27', flag: '🇿🇦' },
];

/** The default country for the WhatsApp panel (Brazil, +55). */
export const DEFAULT_COUNTRY: CountryCode = COUNTRY_CODES[0];
